import { conditionalRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { DateRangeInput } from '@repo/ui/date-range-input'
import DateStringInput from '@repo/ui/date-string-input'
import Input from '@repo/ui/input'
import MultiSelectInput from '@repo/ui/multiselect-input'
import SelectInput from '@repo/ui/select-input'
import { useQuery } from '@tanstack/react-query'

import { getCountries } from '../../api/countries/get-countries'
import { getPeriods } from '../../api/periods/get-periods'
import useTranslate from '../../lib/hooks/use-translate'
import { RadFolderEnum, RadFolderEnumMapping } from '../../lib/types/enums/rad/rad-folder.enum'
import { RadFilters } from '../../lib/types/models/rad/rad'
import { yearsFilterOptions } from '../pcr-personal-space/utils'

type Props = {
  filter?: RadFilters
  onFilterChange: (filter: RadFilters) => void
}

const RadFilter: React.FC<Props> = ({ filter, onFilterChange }) => {
  const { currentLanguage } = useTranslate()
  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  return (
    <div className="flex flex-col gap-4 pt-2">
      <SelectInput
        label="Type de pli"
        options={Object.values(RadFolderEnum).map((option) => ({
          label: RadFolderEnumMapping[option],
          value: option
        }))}
        selectedOption={conditionalRender(!!filter?.type, {
          label: RadFolderEnumMapping[filter?.type as RadFolderEnum],
          value: filter?.type
        })}
        onChange={(type) => {
          onFilterChange({ ...filter, type })
        }}
      />

      <SelectInput
        label="Année"
        optional
        options={yearsFilterOptions}
        selectedOption={
          filter?.year ? { label: filter.year.toString(), value: filter.year } : undefined
        }
        onChange={(year) => onFilterChange({ ...filter, year: year ?? undefined })}
      />

      <DateStringInput
        language={currentLanguage}
        label={'Date de RAD'}
        value={filter?.date}
        onChange={(date) => onFilterChange({ ...filter, date })}
      />

      <Input
        type="text"
        label="Nom de lieu"
        value={filter?.location}
        onInput={(e) =>
          onFilterChange({ ...filter, location: (e.target as HTMLInputElement).value })
        }
      />

      <MultiSelectInput
        searchable
        selectedOptions={filter?.periods?.map((period) => ({
          label: periods?.data.find((p) => p.name === period)?.name || '',
          value: period
        }))}
        variant="primary"
        onChange={(periods) => onFilterChange({ ...filter, periods })}
        options={
          periods?.data.map((period) => ({
            label: period.name,
            value: period.name
          })) || []
        }
        label="Périodes"
      />

      <MultiSelectInput
        allowCustomOptions
        searchable
        selectedOptions={filter?.countries?.map((country) => ({
          label: countries?.data.find((c) => c.name === country)?.name || '',
          value: country
        }))}
        variant="primary"
        onChange={(countries) => {
          onFilterChange({ ...filter, countries })
        }}
        options={
          countries?.data.map((country) => ({
            label: country.name,
            value: country.name
          })) || []
        }
        label="Pays"
      />

      <DateRangeInput
        label="Départ"
        value={
          filter?.departure?.startDate && filter?.departure?.endDate
            ? {
                from: DateFormatter.toDate(filter.departure.startDate),
                to: DateFormatter.toDate(filter.departure.endDate)
              }
            : undefined
        }
        onClearDate={() => onFilterChange({ ...filter, departure: undefined })}
        onDateRangeChange={(dateRange) => {
          if (dateRange?.from && dateRange?.to)
            onFilterChange({
              ...filter,
              departure: {
                startDate: DateFormatter.toDateFormatted(dateRange?.from),
                endDate: DateFormatter.toDateFormatted(dateRange?.to)
              }
            })
        }}
      />
    </div>
  )
}

export default RadFilter
